import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Server, Plus, Pencil, Trash2, CheckCircle, XCircle, Loader2, Globe, Key, ExternalLink,
} from 'lucide-react';
import ConfirmModal from '../ConfirmModal';
import BucketForm from './BucketForm';

const DashboardSettings = () => {
  // ─── Buckets ───
  const [buckets, setBuckets] = useState([]);
  const [bucketsLoading, setBucketsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editBucket, setEditBucket] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [testing, setTesting] = useState(null);
  const [testResult, setTestResult] = useState({});

  useEffect(() => {
    fetchBuckets();
  }, []);

  // ─── Bucket handlers ───
  const fetchBuckets = async () => {
    try { const res = await axios.get('/api/buckets'); setBuckets(res.data); } catch {} finally { setBucketsLoading(false); }
  };

  const handleTest = async (bucket) => {
    setTesting(bucket.id);
    setTestResult((prev) => ({ ...prev, [bucket.id]: null }));
    try {
      await axios.get(`/api/buckets/${bucket.id}/test`);
      setTestResult((prev) => ({ ...prev, [bucket.id]: 'success' }));
      toast.success(`${bucket.name} connected successfully`);
    } catch (err) {
      setTestResult((prev) => ({ ...prev, [bucket.id]: 'error' }));
      toast.error(err.response?.data?.error || 'Connection failed');
    } finally { setTesting(null); }
  };

  const handleDeleteBucket = async (bucket) => {
    setConfirmDelete(null);
    try {
      await axios.delete(`/api/buckets/${bucket.id}`);
      toast.success('Bucket deleted');
      setBuckets((prev) => prev.filter((b) => b.id !== bucket.id));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed');
    }
  };

  const handleSaveBucket = (bucket) => {
    if (editBucket) {
      setBuckets((prev) => prev.map((b) => b.id === bucket.id ? bucket : b));
    } else {
      setBuckets((prev) => [...prev, bucket]);
    }
    setShowForm(false);
    setEditBucket(null);
  };

  // ─── Loading states ───
  if (bucketsLoading) {
    return (
      <div className="space-y-4 animate-fade-in">
        <div className="skeleton h-7 w-40 rounded-lg" />
        <div className="skeleton h-10 w-60 rounded-lg" />
        <div className="skeleton h-36 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in max-w-3xl">
      <div className="mb-6">
        <h2 className="font-heading font-bold text-lg text-foreground">Settings</h2>
        <p className="text-sm text-muted-foreground mt-0.5">Manage storage buckets</p>
      </div>

      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">Cloudflare R2 storage connections</p>
        <button onClick={() => { setEditBucket(null); setShowForm(true); }} className="btn-primary btn-sm">
          <Plus size={14} /> Add Bucket
        </button>
      </div>

          {showForm && (
            <BucketForm
              bucket={editBucket}
              onSave={handleSaveBucket}
              onCancel={() => { setShowForm(false); setEditBucket(null); }}
            />
          )}

          {buckets.length === 0 ? (
            <div className="card py-16 text-center">
              <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Server size={24} className="text-primary" />
              </div>
              <h3 className="font-heading font-semibold text-sm text-foreground mb-1">No buckets configured</h3>
              <p className="text-muted-foreground text-xs max-w-xs mx-auto mb-4">Add a Cloudflare R2 bucket to start storing photos in the cloud.</p>
              <button onClick={() => setShowForm(true)} className="text-sm font-medium text-primary hover:text-primary/80 cursor-pointer transition-colors">
                Add your first bucket
              </button>
            </div>
          ) : (
            <div className="space-y-3 stagger">
              {buckets.map((b) => (
                <div key={b.id} className="card-hover p-5 group">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 bg-primary/10 border border-primary/20 rounded-xl flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                      <Server size={18} className="text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-heading font-semibold text-sm text-foreground truncate">{b.name}</h3>
                        {testResult[b.id] === 'success' && (
                          <span className="badge-success"><CheckCircle size={10} /> Connected</span>
                        )}
                        {testResult[b.id] === 'error' && (
                          <span className="badge-destructive"><XCircle size={10} /> Failed</span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                          <Globe size={10} /> {b.endpoint?.replace('https://', '').split('.')[0]}
                        </span>
                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                          <Key size={10} /> {b.bucket}
                        </span>
                      </div>
                      {b.publicUrl && (
                        <a href={b.publicUrl} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80 mt-2 transition-colors">
                          {b.publicUrl} <ExternalLink size={10} />
                        </a>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => handleTest(b)} disabled={testing === b.id} className="btn-outline btn-sm">
                        {testing === b.id ? <Loader2 size={12} className="animate-spin" /> : 'Test'}
                      </button>
                      <button onClick={() => { setEditBucket(b); setShowForm(true); }} className="btn-ghost btn-icon btn-sm" aria-label="Edit bucket">
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => setConfirmDelete(b)}
                        className="btn-ghost btn-icon btn-sm text-muted-foreground hover:text-destructive hover:bg-destructive/5" aria-label="Delete bucket">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <ConfirmModal
            open={!!confirmDelete}
            title="Delete bucket?"
            message={`"${confirmDelete?.name}" will be permanently removed. Photos stored in this bucket must be deleted first.`}
            confirmLabel="Delete"
            onConfirm={() => confirmDelete && handleDeleteBucket(confirmDelete)}
            onCancel={() => setConfirmDelete(null)}
          />
    </div>
  );
};

export default DashboardSettings;
